import type { UnifiedAuditRow } from '@/api/console';
import { useTranslation } from 'react-i18next';
import { ACTION_LABELS, TARGET_TYPE_LABELS } from './actionLabels';

/**
 * EAA-1821: pulled out of `AuditTable.tsx` into their own module so both
 * `AuditTable` (the list) and `AuditDetailDrawer` (the row detail) can import
 * these downward instead of `AuditDetailDrawer` importing back from
 * `AuditTable` — that reverse import was a real A→B, B→A cycle (confirmed by
 * a read-only convention audit) even though it happened to work today
 * (hoisted function declarations, no top-level side effects). Component-only
 * exports here (no plain constants) so React Fast Refresh keeps working on
 * this file — `RESULT_STATUS_CONFIG` moved to `actionLabels.ts` instead,
 * which was already the plain-data home for this screen's label maps.
 */

/**
 * EAA-1820: "Người thao tác" shows a human label (username or machine label),
 * never a raw Keycloak sub on the audit table — subs stay server-side only.
 */
export function ActorCell({ row }: { row: UnifiedAuditRow }) {
    const { t } = useTranslation();
    const label = row.actor_label?.trim();
    if (label) {
        return <span className="portal-audit-actor">{label}</span>;
    }
    if (!row.actor_sub) {
        return <span className="portal-audit-actor-empty">—</span>;
    }
    return (
        <span className="portal-audit-actor-empty">
            {t('console.accounts.auditPanel.unknownActor')}
        </span>
    );
}

/**
 * EAA-1821: the action cell never shows a bare code. A mapped value renders
 * its Vietnamese label with the raw code beneath in 10px mono (so support can
 * still grep logs); an unmapped value falls back to the raw code ALONE
 * rather than a guessed label — see actionLabels.ts's `ACTION_LABELS`.
 */
export function ActionCell({ action }: { action: string }) {
    const label = ACTION_LABELS[action];
    return (
        <span className="portal-audit-action">
            <span>{label ?? action}</span>
            {label && <code className="portal-audit-action-code">{action}</code>}
        </span>
    );
}

/**
 * EAA-1821: `UnifiedAuditRow.target` is a plain string already built by the
 * shared mapper (`toUnifiedFromAdminAudit` in api/console.ts) as
 * `${target_type}:${target_id}` for admin rows. This only reformats that
 * string for display — it does not redefine or duplicate the mapper.
 */
export function TargetCell({ row }: { row: UnifiedAuditRow }) {
    if (row.kind !== 'admin') {
        return <span>{row.target || '—'}</span>;
    }
    const separatorIndex = row.target.indexOf(':');
    const type = separatorIndex >= 0 ? row.target.slice(0, separatorIndex) : row.target;
    const id = separatorIndex >= 0 ? row.target.slice(separatorIndex + 1) : '';
    return (
        <span className="portal-audit-target">
            <span>{TARGET_TYPE_LABELS[type] ?? type}</span>
            {id && <code className="portal-audit-target-id">{id}</code>}
        </span>
    );
}
